import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Test mode (spec §15) is a first-class feature, not debug code.
// Enabled by `?mode=test` on first load OR `localStorage.somesay_test_mode === 'on'`
// (set by `?mode=test` and removed by the panel's "exit test mode").
//
// All test-mode state — mock GPS, mock time, active player override —
// lives here. Components opt in via useTestMode() rather than reaching
// straight at navigator.geolocation or new Date().

const STORAGE_KEY = 'somesay_test_mode';
const STATE_KEY = 'somesay_test_mode_state';

const defaults = {
  active: false,
  mockLocation: null, // { lat, lng, label } | null — null means real GPS
  mockTimeIso: null, // '2026-05-26T08:39:00-08:00' | null — null means real now
};

const TestModeContext = createContext({
  ...defaults,
  setActive: () => {},
  setMockLocation: () => {},
  setMockTimeIso: () => {},
  reset: () => {},
  now: () => new Date(),
});

function readPersistedActive() {
  if (typeof window === 'undefined') return false;
  if (window.localStorage.getItem(STORAGE_KEY) === 'on') return true;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('mode') === 'test') {
      window.localStorage.setItem(STORAGE_KEY, 'on');
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function readPersistedState() {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(STATE_KEY) || '{}');
  } catch {
    return {};
  }
}

function persistState(state) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STATE_KEY, JSON.stringify(state));
}

export function TestModeProvider({ children }) {
  const [active, setActive] = useState(() => readPersistedActive());
  const [persisted, setPersisted] = useState(() => readPersistedState());

  // Persist toggle changes.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (active) window.localStorage.setItem(STORAGE_KEY, 'on');
    else window.localStorage.removeItem(STORAGE_KEY);
  }, [active]);

  const setMockLocation = useCallback(
    (loc) => {
      const next = { ...persisted, mockLocation: loc };
      setPersisted(next);
      persistState(next);
    },
    [persisted]
  );

  const setMockTimeIso = useCallback(
    (iso) => {
      const next = { ...persisted, mockTimeIso: iso };
      setPersisted(next);
      persistState(next);
    },
    [persisted]
  );

  const reset = useCallback(() => {
    setPersisted({});
    persistState({});
    setActive(false);
  }, []);

  const now = useCallback(() => {
    if (active && persisted.mockTimeIso) {
      const d = new Date(persisted.mockTimeIso);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return new Date();
  }, [active, persisted.mockTimeIso]);

  const value = useMemo(
    () => ({
      active,
      mockLocation: active ? persisted.mockLocation || null : null,
      mockTimeIso: active ? persisted.mockTimeIso || null : null,
      setActive,
      setMockLocation,
      setMockTimeIso,
      reset,
      now,
    }),
    [active, persisted.mockLocation, persisted.mockTimeIso, setMockLocation, setMockTimeIso, reset, now]
  );

  return <TestModeContext.Provider value={value}>{children}</TestModeContext.Provider>;
}

export function useTestMode() {
  return useContext(TestModeContext);
}

// Helper: return the right "now" — mock if active, else real. Components
// that don't want the full hook can call this when given an instance.
export function nowFromMode(mode) {
  return mode?.now ? mode.now() : new Date();
}
